import { create } from "zustand";
import { supabase } from "@/config/supabaseClient";
import api from "@/config/axiosConfig";
import { Notification } from "@/types/notifications";

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  fetchNotifications: (userId: string) => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  updateNotifications: (notifications: Notification[]) => void;
  setupSubscription: (userId: string) => () => void;
}

const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  initialized: false,

  fetchNotifications: async (userId: string) => {
    // If already initialized, don't fetch again
    if (get().initialized) return;

    set({ isLoading: true });
    try {
      const response = await api.social.get("/notifications", {
        params: { userId },
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch notifications");
      }

      const { notifications, unreadCount } = response.data;
      set({
        notifications,
        unreadCount,
        isLoading: false,
        initialized: true,
      });
    } catch (err) {
      set({
        error: "Failed to fetch notifications",
        isLoading: false,
      });
    }
  },

  markAsRead: async (notificationIds: string[]) => {
    try {
      const response = await api.social.post("/notifications/mark-read", {
        notificationIds,
      });

      if (response.status === 200) {
        set((state) => ({
          notifications: state.notifications.map((notification) => ({
            ...notification,
            is_read: notificationIds.includes(notification.id)
              ? true
              : notification.is_read,
          })),
          unreadCount: Math.max(0, state.unreadCount - notificationIds.length),
        }));
      }
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
      throw new Error("Failed to mark notifications as read");
    }
  },

  updateNotifications: (newNotifications: Notification[]) => {
    set({
      notifications: newNotifications,
      unreadCount: newNotifications.filter((n) => !n.is_read).length,
    });
  },

  setupSubscription: (userId: string) => {
    const subscription = supabase
      .channel("notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        () => {
          // Refresh notifications when new one arrives
          get().fetchNotifications(userId);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  },
}));

export default useNotificationsStore;
